import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { sortAssets } from '@/_pages/interwill/editing/components/EditingContent/components/AssetsBlock/helpers';
import { AssetCategoryToText } from '@/entities/assets';
import { useIwEditingStore } from '@/entities/interwills';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { numbers } from '@/shared/utils/numbers';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AssetDistributionModalProps } from '@/widgets/modals/types';

import { ModalContainerV5 } from '../containers/ModalContainerV5';

export const AssetDistributionModal: FC<AssetDistributionModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'assetDistribution' });
  const { t: tc } = useTranslation('assets');

  const modals = useModalsStore();
  const { assets, distribution, beneficiaries } = useIwEditingStore();

  const assetsWithCategoryIndex = useMemo(() => {
    const categoryIndex: Record<string, number> = {};
    return sortAssets(assets).map((asset) => {
      categoryIndex[asset.category] = (categoryIndex[asset.category] || 0) + 1;
      return { ...asset, displayIndex: categoryIndex[asset.category] };
    });
  }, [assets]);

  const getName = useCallback(
    (id?: DtoId) => {
      if (!id) return undefined;
      const beneficiaryIndex = beneficiaries.findIndex((item) => item.id === id);
      if (beneficiaryIndex === -1) return undefined;
      return t('beneficiary', {
        firstName: beneficiaries[beneficiaryIndex].firstName,
        index: beneficiaryIndex + 1,
      });
    },
    [distribution, beneficiaries],
  );

  return (
    <ModalContainerV5 title={t('title')}>
      <Container>
        <TableWrapper>
          <Table>
            <TableName>{t('table')}</TableName>
            <HeaderRow>
              <Cell>{t('asset')}</Cell>
              <Cell>{t('primary')}</Cell>
              <Cell>{t('substitute')} 1</Cell>
              <Cell>{t('substitute')} 2</Cell>
              <Cell>{t('share')}</Cell>
            </HeaderRow>
            {assetsWithCategoryIndex.map((asset) => (
              <Row key={asset.id}>
                <Cell style={{ alignItems: 'flex-start' }}>
                  <span>
                    {tc(AssetCategoryToText[asset.category])} {asset.displayIndex}{' '}
                    <AssetName>({asset.name || asset.nickName || asset.nickname})</AssetName>
                  </span>
                </Cell>
                <Col>
                  {distribution[asset.id].map((item, i) => (
                    <Row key={i}>
                      <CellWide>{getName(item.primaryBeneficiary)}</CellWide>
                      <CellWide>{getName(item.firstSubstitute) ?? t('no')} </CellWide>
                      <CellWide>{getName(item.secondSubstitute) ?? t('no')}</CellWide>
                      <CellWide>
                        {numbers.formatPercent({ value: Number(item.percentage), withSign: true })}
                      </CellWide>
                    </Row>
                  ))}
                </Col>
              </Row>
            ))}
          </Table>
        </TableWrapper>
        <Buttons>
          <ButtonIw
            onClick={() => {
              modals.closeModal();
            }}
          >
            {t('ok')}
          </ButtonIw>
        </Buttons>
      </Container>
    </ModalContainerV5>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`;

const TableWrapper = styled.div`
  overflow: auto;
  width: 100%;
`;

const Table = styled.div`
  width: 100%;
  height: 100%;
  font-size: 14px;
  min-width: 674px;
`;

const TableName = styled.div`
  background: ${COLORS.systemLight200};
  width: 100%;
  padding: 4px 12px;
`;

const Row = styled.div`
  display: flex;
`;

const HeaderRow = styled(Row)`
  font-size: 12px;
  color: ${COLORS.systemDark200};
`;

const Cell = styled.div`
  padding: 8px 12px;
  border: 1px solid ${COLORS.systemLight300};
  display: flex;
  align-items: center;
  flex-basis: 20%;
`;

const CellWide = styled(Cell)`
  flex-basis: 25%;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
  margin-top: 16px;
  ${media.iwMobile} {
    flex-direction: column;
  }
`;

const AssetName = styled.span`
  color: ${COLORS.systemDark200};
`;

const Col = styled.div`
  flex-basis: 80%;
  flex-shrink: 1;
  flex-direction: column;
`;
